"use client";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface RevealProps {
  /** stagger delay before the pop-up plays, in ms. Default 0. */
  delayMs?: number;
  /** wrapper element tag. Default "div". */
  as?: "div" | "section" | "li" | "span";
  className?: string;
  children: React.ReactNode;
}

/**
 * Reveal — IntersectionObserver wrapper that adds the `.revealed` class once the
 * element scrolls into view, driving a fade + pop-up (see `.reveal` in globals.css).
 * Fires once, then disconnects. Named export (also default).
 */
export function Reveal({ delayMs = 0, as = "div", className, children }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShown(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const Tag = as as "div";
  return (
    <Tag
      ref={ref}
      className={cn("reveal", shown && "revealed", className)}
      style={{ transitionDelay: shown ? `${delayMs}ms` : undefined }}
    >
      {children}
    </Tag>
  );
}

export default Reveal;
